import React, { useState } from 'react'
import { Alert, Box, Button, Collapse, Grid, LinearProgress, Link, MenuItem, Stack, TextField, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useSnackbar } from 'notistack'
import { SectionCard, Pill, ActionIconBtn, helperProps } from '../components/common'
import DataTable from '../components/Tables'
import { openS3Doc } from '../s3Doc'
import { fmtDateTime } from '../apiMap'
import {
  useListStandardCardsQuery, useAddStandardCardMutation, useDeleteStandardCardMutation,
} from '../../../redux/d2cApiSlice'

// The slab decision: Blended / Courier-wise are tiered by monthly volume (the 5 named tiers
// below, mirroring D2cProposalConfigService.STANDARD_TIERS); SDD / NDD / H&B are single-band
// (gate is pickup density, not volume); RVP attaches to a forward format.
const FORMATS = [
  { key: 'BLENDED', label: 'Blended', banded: true },
  { key: 'COURIER_WISE', label: 'Courier-wise', banded: true },
  { key: 'SDD', label: 'SDD' },
  { key: 'NDD', label: 'NDD' },
  { key: 'HNB', label: 'H&B' },
  { key: 'RVP', label: 'RVP', rvp: true },
]
// Sales' plan tiers (feedback D2, Sep 2026). Inclusive bounds with +1 edges — the sheet writes
// 0–1000 / 1000–2500 / …, so a boundary volume belongs to the LOWER tier. The backend validates
// uploads against the same list and derives tierName from the band, so keep the two in step.
// Platinum is capped at 45,000: above that standard paper ends (custom pricing desk).
const BANDS = [
  { tier: 'Startups', label: 'Startups · 0 – 1,000', min: 0, max: 1000 },
  { tier: 'Accelerate', label: 'Accelerate · 1,001 – 2,500', min: 1001, max: 2500 },
  { tier: 'Silver', label: 'Silver · 2,501 – 5,000', min: 2501, max: 5000 },
  { tier: 'Gold', label: 'Gold · 5,001 – 10,000', min: 5001, max: 10000 },
  { tier: 'Platinum', label: 'Platinum · 10,001 – 45,000', min: 10001, max: 45000 },
]
// Tier name for a stored card: the backend's derived tierName first; the local ladder as a
// fallback for anything saved before tierName existed. Null = not a tiered card.
const tierOf = (r) => r.tierName || (BANDS.find((b) => b.min === r.minShipments && b.max === r.maxShipments) || {}).tier || null
const RVP_FOR = [['BLENDED', 'Blended'], ['COURIER_WISE', 'Courier-wise'], ['SDD', 'SDD'], ['NDD', 'NDD'], ['HNB', 'H&B']]
const FMT_LABEL = { BLENDED: 'Blended', COURIER_WISE: 'Courier-wise', SDD: 'SDD', NDD: 'NDD', HNB: 'H&B', RVP: 'RVP' }
const OK_EXT = ['xlsx', 'xls', 'csv', 'pdf']
const STALE_MS = 1000 * 60 * 60 * 24 * 183 // ~6 months

/* Standard Rate Cards (FH master) — the pre-decided cards the Standard proposal flow serves as-is.
   One atomic multipart upload (file + metadata) to the real /masters/standard-rate-cards API.
   Edit = delete + re-add (a PUT can't convert a slot's shape; the backend also blocks that). */
export default function StandardRateCardsAdmin() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: cards = [], isLoading, isError, error } = useListStandardCardsQuery()
  const [addCard, addState] = useAddStandardCardMutation()
  const [deleteCard] = useDeleteStandardCardMutation()

  const [open, setOpen] = useState(false)
  const [file, setFile] = useState(null)
  const [cardFormat, setCardFormat] = useState('BLENDED')
  const [bandIdx, setBandIdx] = useState(0)
  const [rvpFor, setRvpFor] = useState('BLENDED')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [showErr, setShowErr] = useState(false)
  const busy = addState.isLoading

  const fmt = FORMATS.find((f) => f.key === cardFormat) || {}
  const reset = () => { setFile(null); setCardFormat('BLENDED'); setBandIdx(0); setRvpFor('BLENDED'); setEffectiveFrom(''); setShowErr(false) }

  // Mirror the backend's file guard so the user gets the error before the round-trip.
  const fileErr = (() => {
    if (!file) return 'A file is required.'
    const ext = (file.name.split('.').pop() || '').toLowerCase()
    if (!OK_EXT.includes(ext)) return `File must be one of: ${OK_EXT.join(', ')}.`
    if (file.size > 10 * 1024 * 1024) return 'File exceeds the 10MB limit.'
    return null
  })()

  const submit = async () => {
    setShowErr(true)
    if (fileErr) return
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('cardFormat', cardFormat)
      if (fmt.banded) { fd.append('minShipments', String(BANDS[bandIdx].min)); fd.append('maxShipments', String(BANDS[bandIdx].max)) }
      if (fmt.rvp) fd.append('rvpFor', rvpFor)
      if (effectiveFrom) fd.append('effectiveFrom', effectiveFrom) // yyyy-MM-dd
      await addCard(fd).unwrap()
      enqueueSnackbar('Standard rate card uploaded.', { variant: 'success' })
      setOpen(false); reset()
    } catch (e) {
      // Occupied-slot / non-canonical-band messages are user-facing — surface them verbatim.
      enqueueSnackbar(e?.data?.message || e?.data?.error || e?.message || 'Upload failed (Functional-Head only).', { variant: 'error' })
    }
  }

  const remove = async (r) => {
    try { await deleteCard(r.id).unwrap(); enqueueSnackbar('Standard rate card deactivated.', { variant: 'info' }) }
    catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Delete failed (Functional-Head only).', { variant: 'error' }) }
  }

  const scopeText = (r) => {
    if (r.cardFormat === 'RVP') return `→ ${FMT_LABEL[r.rvpFor] || r.rvpFor || '—'}`
    if (r.minShipments != null && r.maxShipments != null) {
      const range = `${Number(r.minShipments).toLocaleString('en-IN')} – ${Number(r.maxShipments).toLocaleString('en-IN')}`
      const tier = tierOf(r)
      // A band that isn't on the current ladder (pre-tier upload) is flagged so the FH re-uploads it.
      return tier ? `${tier} · ${range}` : `${range} ⚠ not a current tier`
    }
    return 'All volumes'
  }
  const isStale = (r) => r.effectiveFrom && (Date.now() - new Date(r.effectiveFrom).getTime() > STALE_MS)

  const cols = [
    { key: 'format', label: 'Format', render: (r) => <Pill label={FMT_LABEL[r.cardFormat] || r.cardFormat} styleKey={r.cardFormat === 'RVP' ? 'orange' : 'purple'} /> },
    { key: 'scope', label: 'Tier / scope', render: (r) => scopeText(r) },
    { key: 'file', label: 'File', render: (r) => (r.fileUri || r.fileName
      ? <Link component="button" underline="hover" sx={{ fontSize: 12, whiteSpace: 'nowrap' }} onClick={() => openS3Doc(r.fileUri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>{r.fileName || 'Open'} ↗</Link>
      : <span style={{ fontSize: 12 }}>—</span>) },
    { key: 'eff', label: 'Effective from', render: (r) => <span>{r.effectiveFrom ? fmtDateTime(r.effectiveFrom) : '—'}{isStale(r) ? '  ⚠ stale' : ''}</span> },
    { key: 'a', label: 'Action', align: 'right', render: (r) => <ActionIconBtn title="Deactivate" color="error" onClick={() => remove(r)}><DeleteOutlineIcon fontSize="small" /></ActionIconBtn> },
  ]

  return (
    <SectionCard
      title="Standard Rate Cards"
      subtitle="The pre-decided cards the proposal wizard's Standard commercial serves — one active card per slot (format + volume tier, or RVP + attaches-to). Uploaded by the Functional Head, downloaded by Sales Leaders and sent to clients as-is."
      action={<Button variant="outlined" sx={{ textTransform: 'none', borderRadius: '6px' }} onClick={() => (open ? (setOpen(false), reset()) : setOpen(true))}>{open ? 'Close' : '＋ Upload card'}</Button>}
    >
      <Collapse in={open} unmountOnExit>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 2, mb: 2, bgcolor: '#FAFAFC' }}>
          <Alert severity="warning" sx={{ fontSize: 12, mb: 1.5 }}>This file is downloadable by all Sales Leaders as-is and shared with clients — never upload an internal-cost workbook.</Alert>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth select size="small" label="Format" value={cardFormat} onChange={(e) => setCardFormat(e.target.value)}>
                {FORMATS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
              </TextField>
            </Grid>
            {fmt.banded && (
              <Grid item xs={12} md={4}>
                <TextField fullWidth select size="small" label="Plan tier (monthly orders)" value={bandIdx} onChange={(e) => setBandIdx(Number(e.target.value))}>
                  {BANDS.map((b, i) => <MenuItem key={b.label} value={i}>{b.label}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            {fmt.rvp && (
              <Grid item xs={12} md={4}>
                <TextField fullWidth select size="small" label="Attaches to" value={rvpFor} onChange={(e) => setRvpFor(e.target.value)}>
                  {RVP_FOR.map(([v, t]) => <MenuItem key={v} value={v}>{t}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="date" label="Effective from" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} InputLabelProps={{ shrink: true }} {...helperProps('Optional. Shown on the card list; stale after ~6 months.')} />
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant={file ? 'contained' : 'outlined'} component="label" size="small" sx={{ textTransform: 'none', borderRadius: '6px' }}>
                  {file ? `📎 ${file.name}` : '📎 Choose file'}
                  <input type="file" hidden accept=".xlsx,.xls,.csv,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </Button>
                {file && <Button size="small" variant="text" sx={{ textTransform: 'none' }} onClick={() => setFile(null)}>Clear</Button>}
                <Typography sx={{ fontSize: 11, color: showErr && fileErr ? 'error.main' : 'text.secondary' }}>{showErr && fileErr ? fileErr : 'xlsx · xls · csv · pdf · max 10MB'}</Typography>
              </Stack>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="contained" sx={{ textTransform: 'none', borderRadius: '6px' }} disabled={busy} onClick={submit}>{busy ? 'Uploading…' : '⬆ Upload card'}</Button>
            <Button variant="text" sx={{ textTransform: 'none' }} onClick={() => { setOpen(false); reset() }}>Cancel</Button>
          </Stack>
        </Box>
      </Collapse>

      {isLoading && <LinearProgress sx={{ my: 1 }} />}
      {isError && <Alert severity="error" sx={{ my: 1 }}>{error?.message || 'Failed to load the standard rate cards.'}</Alert>}
      <DataTable columns={cols} rows={cards} getRowKey={(r) => r.id} emptyText={isLoading ? 'Loading…' : 'No standard rate cards uploaded yet.'} />
    </SectionCard>
  )
}
